// src/context/AuthContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';
import { auth, db } from '../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isBoard, setIsBoard] = useState(false);
  const [isMember, setIsMember] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const [isBanned, setIsBanned] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [approvalStatus, setApprovalStatus] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [userData, setUserData] = useState(null);

  // Function to update last seen timestamp
  const updateLastSeen = async (userId) => {
    try {
      await updateDoc(doc(db, 'users', userId), {
        lastSeen: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating last seen:', error);
    }
  };

  // Function to create missing user document
  const createMissingUserDocument = async (user) => {
    if (!user) return null;
    
    try {
      console.log('Creating missing user document for:', user.uid);
      
      const newUserData = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || user.email?.split('@')[0] || 'User',
        name: user.displayName || user.email?.split('@')[0] || 'User',
        role: 'member',
        
        // Approval status
        approved: false,
        isApproved: false,
        registrationStatus: 'pending_approval',
        approvedBy: null,
        approvedAt: null,
        registrationComplete: true,
        
        // Account status
        isActive: true,
        isBlocked: false,
        isBanned: false,
        isAdmin: false,
        
        // 2FA status
        twoFactorEnabled: false,
        twoFactorEmail: null,
        twoFactorSetupAt: null,
        
        // Contact info
        phone: "",
        company: "",
        position: "",
        
        // Profile fields
        education: "",
        certifications: "",
        skills: "",
        experience: "",
        bio: "",
        photoUrl: null,
        
        // Privacy settings
        emailNotifications: true,
        pushNotifications: true,
        profileVisibility: 'public',
        
        // Timestamps
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastLoginAt: serverTimestamp(),
        lastSeen: serverTimestamp()
      };
      
      await setDoc(doc(db, 'users', user.uid), newUserData);
      console.log('User document created successfully');
      return newUserData;
      
    } catch (error) {
      console.error('Error creating user document:', error);
      return null;
    }
  };

  // Function to check user access (allows unapproved users to stay logged in)
  const checkUserAccess = async (user) => {
    if (!user) return { hasAccess: false, userData: null };
    
    try {
      const userDocRef = doc(db, 'users', user.uid);
      let userDoc = await getDoc(userDocRef);
      let data = null;
      
      // If user document doesn't exist, create it
      if (!userDoc.exists()) {
        console.log('User document not found, creating one...');
        data = await createMissingUserDocument(user);
        if (data) {
          userDoc = await getDoc(userDocRef);
        }
      } else {
        data = userDoc.data();
      }
      
      if (!data) {
        console.error('Could not create or find user document');
        setAuthError('Account setup incomplete. Please contact support.');
        return { hasAccess: false, userData: null };
      }
      
      const isApprovedFlag = data.approved === true || 
                             data.isApproved === true || 
                             data.registrationStatus === 'approved';
      const isPendingFlag = data.registrationStatus === 'pending_approval' || 
                            (!isApprovedFlag && data.approved === false);
      const isRejectedFlag = data.registrationStatus === 'rejected';
      const isBannedFlag = data.isBanned === true || data.isBlocked === true;
      const isActiveFlag = data.isActive !== false;
      const role = data.role || 'member';
      
      // Check for banned account
      if (isBannedFlag) {
        console.warn('User is banned:', user.uid);
        setAuthError('Your account has been banned. Please contact support.');
        return { hasAccess: false, userData: data };
      }
      
      // Check for inactive account
      if (!isActiveFlag) {
        console.warn('User account is deactivated:', user.uid);
        setAuthError('Your account has been deactivated. Please contact an administrator.');
        return { hasAccess: false, userData: data };
      }
      
      // Check for rejected account
      if (isRejectedFlag) {
        console.warn('User registration was rejected:', user.uid);
        setAuthError('Your registration was rejected. Please contact support for more information.');
        return { hasAccess: false, userData: data };
      }
      
      // ALLOW unapproved users to stay logged in (but with restricted access)
      if (isPendingFlag) {
        console.log('User pending approval:', user.uid);
        setAuthError(null);
        return { hasAccess: true, userData: data, isPending: true };
      }
      
      // Approved users have full access
      setAuthError(null);
      return { hasAccess: true, userData: data };
      
    } catch (error) {
      console.error('Error checking user access:', error);
      setAuthError('Error verifying account. Please try again later.');
      return { hasAccess: false, userData: null };
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setLoading(true);
      setCurrentUser(user);
      
      if (user) {
        const { hasAccess, userData: userDocData, isPending } = await checkUserAccess(user);
        
        if (hasAccess && userDocData) {
          const data = userDocData;
          const role = data.role || 'member';
          const isApprovedFlag = data.approved === true || 
                                 data.isApproved === true || 
                                 data.registrationStatus === 'approved';
          const isPendingFlag = data.registrationStatus === 'pending_approval' || 
                                (!isApprovedFlag && data.approved === false);
          const isRejectedFlag = data.registrationStatus === 'rejected';
          const isBannedFlag = data.isBanned === true || data.isBlocked === true;
          const isActiveFlag = data.isActive !== false;
          const approvalStatusValue = data.registrationStatus || 
                                     (isApprovedFlag ? 'approved' : 
                                      (isPendingFlag ? 'pending_approval' : 
                                       (isRejectedFlag ? 'rejected' : 'unknown')));
          
          setUserRole(role);
          setIsAdmin(role === 'admin' || data.isAdmin === true);
          setIsBoard(role === 'board');
          setIsMember(role === 'member' && !data.isAdmin);
          setIsApproved(isApprovedFlag);
          setIsBanned(isBannedFlag);
          setIsActive(isActiveFlag);
          setApprovalStatus(approvalStatusValue);
          setUserData(data);
          setAuthError(null);
          
          // Update last seen for approved users only (to reduce writes)
          if (isApprovedFlag) {
            updateLastSeen(user.uid);
          }
        } else if (userDocData) {
          // User exists but has no access (banned, inactive, rejected)
          setUserData(userDocData);
          setIsBanned(userDocData.isBanned === true || userDocData.isBlocked === true);
          setIsActive(userDocData.isActive !== false);
          setApprovalStatus(userDocData.registrationStatus || 'unknown');
          // Don't set userRole or other flags for blocked users
          setUserRole(null);
          setIsAdmin(false);
          setIsBoard(false);
          setIsMember(false);
          setIsApproved(false);
        } else {
          // No user data found - should not happen now because we create it
          setUserRole(null);
          setIsAdmin(false);
          setIsBoard(false);
          setIsMember(false);
          setIsApproved(false);
          setIsBanned(false);
          setIsActive(true);
          setApprovalStatus('not_found');
          setUserData(null);
        }
      } else {
        // No user logged in
        setUserRole(null);
        setIsAdmin(false);
        setIsBoard(false);
        setIsMember(false);
        setIsApproved(false);
        setIsBanned(false);
        setIsActive(true);
        setApprovalStatus(null);
        setAuthError(null);
        setUserData(null);
      }
      
      setLoading(false);
    });
    
    return unsubscribe;
  }, []);

  // Logout function that properly cleans up
  const logout = async () => {
    try {
      if (currentUser) {
        await updateDoc(doc(db, 'users', currentUser.uid), {
          lastSeen: serverTimestamp()
        }).catch(console.error);
      }
      await signOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
      await signOut(auth);
    }
  };

  const value = {
    user: currentUser,
    loading,
    userRole,
    isAdmin,
    isBoard,
    isMember,
    isApproved,
    isBanned,
    isActive,
    approvalStatus,
    authError,
    userData,
    logout,
    
    // Helper functions
    canAccessChat: () => {
      if (!currentUser) return false;
      if (isBanned) return false;
      if (!isActive) return false;
      return isApproved || isAdmin || isBoard;
    },
    
    canAccessAdminPanel: () => isAdmin === true,
    
    canAccessBoardPanel: () => isBoard === true || isAdmin === true,
    
    canAccessProfile: () => {
      return currentUser !== null && !isBanned && isActive;
    },
    
    isPendingApproval: () => {
      return approvalStatus === 'pending_approval' || 
             (!isApproved && userData?.approved === false) ||
             (userData?.registrationStatus === 'pending_approval');
    },
    
    isUserApproved: () => {
      return isApproved === true || 
             userData?.approved === true || 
             userData?.isApproved === true ||
             approvalStatus === 'approved';
    },
    
    getApprovalMessage: () => {
      if (approvalStatus === 'pending_approval') {
        return '⏳ Your account is pending admin approval. You will receive full access once approved.';
      }
      if (approvalStatus === 'rejected') {
        return '❌ Your account application was not approved. Please contact support for more information.';
      }
      if (approvalStatus === 'approved') {
        return '✅ Your account is approved. You have full access to the platform.';
      }
      return null;
    },
    
    hasLimitedAccess: () => {
      return !isApproved && !isAdmin && !isBoard && approvalStatus !== 'rejected';
    },
    
    getDisplayName: () => {
      if (currentUser?.displayName) return currentUser.displayName;
      if (userData?.displayName) return userData.displayName;
      if (userData?.name) return userData.name;
      if (currentUser?.email) return currentUser.email.split('@')[0];
      return 'User';
    }
  };
  
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};